class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this.content = this.summaryToggle.nextElementSibling;

    this.detailsContainer.addEventListener(
      'keyup',
      event => event.code.toUpperCase() === 'ESCAPE' && this.close()
    );
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    this.querySelector('button[type="button"]')?.addEventListener('click', this.close.bind(this));

    this.summaryToggle.setAttribute('role', 'button');

    this.handleTimelineStart = this.updateAttributes.bind(this, true);
    this.handleTimelineReverseComplete = this.updateAttributes.bind(this, false);
  }

  connectedCallback() {
    this.buildTimeline();
  }

  disconnectedCallback() {
    this.timeline?.kill();
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay'))
      this.close(false);
  }

  buildTimeline() {
    if (this.timeline) this.timeline.kill();

    this.timeline = gsap
      .timeline({
        paused: true,
        onStart: this.handleTimelineStart,
        onReverseComplete: this.handleTimelineReverseComplete,
      })
      .fromTo(
        this.content,
        {
          scale: 0.7,
          autoAlpha: 0,
        },
        {
          scale: 1,
          autoAlpha: 1,
          duration: 0.2,
          ease: 'power1.inOut',
        }
      );

    if (this.isOpen()) {
      this.timeline.progress(1);
    }
  }

  animate(open = true) {
    if (open) {
      this.timeline?.play();
    } else {
      this.timeline?.reverse();
    }
  }

  open() {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    document.body.addEventListener('click', this.onBodyClickEvent);
    this.summaryToggle.setAttribute('aria-expanded', true);

    this.animate(true);
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    document.body.removeEventListener('click', this.onBodyClickEvent);
    this.summaryToggle.setAttribute('aria-expanded', false);

    this.animate(false);
  }

  updateAttributes(open = true) {
    document.body.classList.toggle('overflow-hidden', open);
    document.body.classList.toggle('has-open-modal', open);

    if (this.id) {
      document.body.classList.toggle(`has-open-${this.id}`, open);
    }

    this.detailsContainer.toggleAttribute('open', open);

    if (open) {
      setTimeout(() => {
        const container = this.detailsContainer.querySelector(
          '[data-focus-container], [tabindex="-1"]'
        );
        const input =
          this.detailsContainer.querySelector('input:not([type="hidden"])') ??
          container.querySelector('a') ??
          container;
        trapFocus(container, input);
      }, 100);
    } else {
      document.body.dispatchEvent(new CustomEvent('modalClosed'));
    }
  }
}

customElements.define('details-modal', DetailsModal);

class SearchModal extends DetailsModal {
  constructor() {
    super();
  }

  buildTimeline() {
    if (this.timeline) this.timeline.kill();

    this.timeline = gsap
      .timeline({
        paused: true,
        onStart: this.handleTimelineStart,
        onReverseComplete: this.handleTimelineReverseComplete,
      })
      .fromTo(
        this.content,
        {
          autoAlpha: 0,
        },
        {
          autoAlpha: 1,
          duration: 0.3,
          ease: 'power2.out',
        }
      );

    if (this.isOpen()) {
      this.timeline.progress(1);
    }
  }

  refreshTrapFocus() {
    const container = this.detailsContainer?.querySelector('[data-focus-container], [tabindex="-1"]') || this.content;
    if (!container) return;

    const input = this.detailsContainer?.querySelector('input:not([type="hidden"])');
    const firstLink = container.querySelector('a');
    const elementToFocus = document.activeElement && container.contains(document.activeElement)
      ? document.activeElement
      : input || firstLink || container;

    // Reapply focus trap with updated focusable elements
    trapFocus(container, elementToFocus, { inputSelection: false });
  }
}

customElements.define('search-modal', SearchModal);
